/**
 * MediaViewer — Immersive fullscreen lightbox for media preview.
 *
 * WhatsApp-style viewer with:
 * - Image: zoom (double-tap, pinch, wheel), pan when zoomed, swipe to navigate
 * - Video: full-screen playback with native controls
 * - PDF: scrollable multi-page viewer (lazy-loaded)
 * - File: info card with download CTA
 * - Navigation: swipe, arrows, keyboard (Esc/ArrowLeft/ArrowRight)
 * - Swipe-down to dismiss on mobile
 * - Overlaid header (close/download) and footer (counter/arrows)
 */

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  memo,
  lazy,
  Suspense,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import type { MediaItem } from '../hooks/useMediaGallery';
import { chatHaptic } from '../utils/chatHaptic';

const PdfFullViewer = lazy(() => import('./PdfFullViewer'));

// =============================================================================
// Types
// =============================================================================

interface MediaViewerProps {
  items: MediaItem[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

// =============================================================================
// Helpers
// =============================================================================

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getTouchDistance(t1: React.Touch, t2: React.Touch): number {
  return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
}

// =============================================================================
// ImageContent — zoom / pan / pinch / swipe / double-tap
// =============================================================================

interface ImageContentProps {
  src: string;
  onNext: (() => void) | null;
  onPrev: (() => void) | null;
  onDismiss: () => void;
}

function ImageContent({ src, onNext, onPrev, onDismiss }: ImageContentProps) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isAnimating, setIsAnimating] = useState(false);

  // Refs for gesture tracking
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastTouchPosRef = useRef<{ x: number; y: number } | null>(null);
  const pinchRef = useRef<{ dist: number; scale: number } | null>(null);
  const lastTapRef = useRef(0);
  const isDraggingRef = useRef(false);
  const pinchHapticRef = useRef<number>(0); // tracks last haptic threshold crossed

  // Double-tap toggle zoom
  const handleDoubleTap = useCallback((clientX: number, clientY: number) => {
    chatHaptic.mediaZoom();
    setIsAnimating(true);
    if (scale > 1) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    } else {
      // Zoom toward tap point
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const tapX = clientX - rect.left;
        const tapY = clientY - rect.top;
        setScale(2);
        setOffset({
          x: (centerX - tapX) / 2,
          y: (centerY - tapY) / 2,
        });
      } else {
        setScale(2);
      }
    }
    setTimeout(() => setIsAnimating(false), 300);
  }, [scale]);

  // Mouse wheel zoom (desktop)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handler = (e: WheelEvent) => {
      e.preventDefault();
      setScale((prev) => {
        const next = prev - e.deltaY * 0.003;
        const clamped = Math.min(Math.max(next, 1), 4);
        if (clamped === 1) setOffset({ x: 0, y: 0 });
        return clamped;
      });
    };

    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch start
      pinchRef.current = {
        dist: getTouchDistance(e.touches[0], e.touches[1]),
        scale,
      };
      pinchHapticRef.current = 0;
    } else if (e.touches.length === 1) {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: Date.now(),
      };
      lastTouchPosRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      isDraggingRef.current = false;
    }
  }, [scale]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchRef.current) {
      // Pinch zoom
      const dist = getTouchDistance(e.touches[0], e.touches[1]);
      const newScale = pinchRef.current.scale * (dist / pinchRef.current.dist);
      const clamped = Math.min(Math.max(newScale, 1), 4);
      setScale(clamped);
      if (clamped === 1) setOffset({ x: 0, y: 0 });
      // Haptic at 1.5x and 2x thresholds (once per gesture)
      const nextThreshold = clamped >= 2 ? 2 : clamped >= 1.5 ? 1.5 : 0;
      if (nextThreshold > pinchHapticRef.current) {
        pinchHapticRef.current = nextThreshold;
        chatHaptic.mediaPinch();
      }
    } else if (e.touches.length === 1 && lastTouchPosRef.current) {
      const dx = e.touches[0].clientX - lastTouchPosRef.current.x;
      const dy = e.touches[0].clientY - lastTouchPosRef.current.y;

      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
        isDraggingRef.current = true;
      }

      if (scale > 1) {
        // Pan when zoomed
        setOffset((prev) => ({ x: prev.x + dx / scale, y: prev.y + dy / scale }));
      }

      lastTouchPosRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }
  }, [scale]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (pinchRef.current) {
      pinchRef.current = null;
      return;
    }

    if (!touchStartRef.current) return;

    const endX = e.changedTouches[0]?.clientX ?? touchStartRef.current.x;
    const endY = e.changedTouches[0]?.clientY ?? touchStartRef.current.y;
    const dx = endX - touchStartRef.current.x;
    const dy = endY - touchStartRef.current.y;
    const elapsed = Date.now() - touchStartRef.current.time;

    if (!isDraggingRef.current) {
      // Tap — check for double-tap
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        handleDoubleTap(endX, endY);
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    } else if (scale === 1 && elapsed < 400) {
      // Swipe detection at 1x zoom
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      if (absDx > 80 && absDx > absDy) {
        // Horizontal swipe → navigate
        if (dx < 0 && onNext) onNext();
        else if (dx > 0 && onPrev) onPrev();
      } else if (dy > 80 && absDy > absDx) {
        // Swipe down → dismiss
        chatHaptic.mediaDismiss();
        onDismiss();
      }
    }

    touchStartRef.current = null;
    lastTouchPosRef.current = null;
    isDraggingRef.current = false;
  }, [scale, onNext, onPrev, onDismiss, handleDoubleTap]);

  // Desktop double-click
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    handleDoubleTap(e.clientX, e.clientY);
  }, [handleDoubleTap]);

  // Desktop drag for pan
  const mouseDragRef = useRef<{ startX: number; startY: number } | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scale > 1) {
      mouseDragRef.current = { startX: e.clientX, startY: e.clientY };
    }
  }, [scale]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (mouseDragRef.current && scale > 1) {
      const dx = e.clientX - mouseDragRef.current.startX;
      const dy = e.clientY - mouseDragRef.current.startY;
      setOffset((prev) => ({ x: prev.x + dx / scale, y: prev.y + dy / scale }));
      mouseDragRef.current = { startX: e.clientX, startY: e.clientY };
    }
  }, [scale]);

  const handleMouseUp = useCallback(() => {
    mouseDragRef.current = null;
  }, []);

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full touch-none items-center justify-center"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <img
        src={src}
        alt=""
        className="max-h-full max-w-full select-none object-contain"
        style={{
          transform: `scale(${scale}) translate(${offset.x}px, ${offset.y}px)`,
          transition: isAnimating ? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
          willChange: 'transform',
        }}
        draggable={false}
      />
    </div>
  );
}

// =============================================================================
// VideoContent
// =============================================================================

function VideoContent({ url }: { url: string }) {
  return (
    <div className="flex h-full w-full items-center justify-center px-4" onClick={(e) => e.stopPropagation()}>
      <video
        src={url}
        controls
        autoPlay
        playsInline
        className="max-h-full max-w-full rounded-lg"
        style={{ maxHeight: 'calc(100vh - 160px)' }}
      />
    </div>
  );
}

// =============================================================================
// FileContent — non-previewable file download card
// =============================================================================

function FileContent({ item }: { item: MediaItem }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full w-full items-center justify-center px-8">
      <div className="w-full max-w-sm rounded-2xl border border-white/20 bg-white/10 p-8 text-center backdrop-blur-md">
        <svg className="mx-auto h-16 w-16 text-white/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
        <h3 className="mt-4 truncate text-lg font-bold text-white">{item.fileName || t('messaging.file')}</h3>
        {item.fileSize && (
          <p className="mt-1 text-sm text-white/50">{formatFileSize(item.fileSize)}</p>
        )}
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-white/20"
          onClick={(e) => e.stopPropagation()}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {t('common.download')}
        </a>
      </div>
    </div>
  );
}

// =============================================================================
// PdfContent wrapper (lazy)
// =============================================================================

function PdfContent({ url, fileName }: { url: string; fileName?: string }) {
  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border border-white/20 border-t-white" />
        </div>
      }
    >
      <PdfFullViewer url={url} fileName={fileName} />
    </Suspense>
  );
}

// =============================================================================
// Navigation Arrows (desktop)
// =============================================================================

function NavArrow({ direction, onClick }: { direction: 'prev' | 'next'; onClick: () => void }) {
  const { t } = useTranslation();
  const isPrev = direction === 'prev';
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); chatHaptic.mediaZoom(); onClick(); }}
      className={`absolute top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white/10 p-3 text-white backdrop-blur-sm transition-all hover:bg-white/20 active:scale-95 md:flex ${
        isPrev ? 'start-4' : 'end-4'
      }`}
      aria-label={isPrev ? t('common.previous') : t('common.next')}
    >
      <svg
        className="h-6 w-6"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ transform: isPrev ? undefined : 'rotate(180deg)' }}
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
  );
}

// =============================================================================
// MediaViewer — Main Component
// =============================================================================

export const MediaViewer = memo(function MediaViewer({
  items,
  initialIndex,
  isOpen,
  onClose,
}: MediaViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [direction, setDirection] = useState(0);

  // Sync index when viewer opens
  useEffect(() => {
    if (isOpen) setCurrentIndex(initialIndex);
  }, [isOpen, initialIndex]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  const currentItem = items[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < items.length - 1;

  const goNext = useCallback(() => {
    if (hasNext) {
      setDirection(1);
      setCurrentIndex((i) => i + 1);
    }
  }, [hasNext]);

  const goPrev = useCallback(() => {
    if (hasPrev) {
      setDirection(-1);
      setCurrentIndex((i) => i - 1);
    }
  }, [hasPrev]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handler = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goPrev();
          break;
        case 'ArrowRight':
          goNext();
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose, goNext, goPrev]);

  // Render content based on type
  const renderContent = useCallback(() => {
    if (!currentItem) return null;

    switch (currentItem.type) {
      case 'image':
        return (
          <ImageContent
            src={currentItem.url}
            onNext={hasNext ? goNext : null}
            onPrev={hasPrev ? goPrev : null}
            onDismiss={onClose}
          />
        );
      case 'video':
        return <VideoContent url={currentItem.url} />;
      case 'pdf':
        return <PdfContent url={currentItem.url} fileName={currentItem.fileName} />;
      default:
        return <FileContent item={currentItem} />;
    }
  }, [currentItem, hasNext, hasPrev, goNext, goPrev, onClose]);

  // Slide animation variants
  const variants = {
    enter: (d: number) => ({
      x: d > 0 ? 200 : d < 0 ? -200 : 0,
      opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({
      x: d > 0 ? -200 : d < 0 ? 200 : 0,
      opacity: 0,
    }),
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && currentItem && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[200] flex flex-col bg-black"
        >
          {/* Header overlay */}
          <div className="absolute inset-x-0 top-0 z-20 flex items-center gap-3 bg-gradient-to-b from-black/70 to-transparent px-4 pb-8 pt-safe">
            <div className="flex h-12 items-center gap-3 pt-2">
              {/* Close button */}
              <button
                type="button"
                onClick={onClose}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              {/* Sender info */}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-white">
                  {currentItem.senderName}
                </div>
                <div className="text-xs text-white/50">
                  {formatDate(currentItem.timestamp)} · {formatTime(currentItem.timestamp)}
                </div>
              </div>

              {/* Download button */}
              <a
                href={currentItem.url}
                target="_blank"
                rel="noopener noreferrer"
                download={currentItem.fileName}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
                onClick={(e) => e.stopPropagation()}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </a>
            </div>
          </div>

          {/* Content area — slides between items */}
          <div className="relative flex-1 overflow-hidden" style={{ paddingTop: 60, paddingBottom: 60 }}>
            <AnimatePresence mode="wait" initial={false} custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="absolute inset-0"
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>

            {/* Desktop navigation arrows */}
            {hasPrev && <NavArrow direction="prev" onClick={goPrev} />}
            {hasNext && <NavArrow direction="next" onClick={goNext} />}
          </div>

          {/* Footer overlay */}
          {items.length > 1 && (
            <div className="absolute inset-x-0 bottom-0 z-20 flex items-center justify-center bg-gradient-to-t from-black/70 to-transparent pb-safe pt-8">
              <div className="flex items-center gap-4 pb-4">
                {/* Counter */}
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold tabular-nums text-white/70 backdrop-blur-sm">
                  {currentIndex + 1} / {items.length}
                </span>

                {/* Filename if file/pdf */}
                {(currentItem.type === 'pdf' || currentItem.type === 'file') && currentItem.fileName && (
                  <span className="max-w-[200px] truncate text-xs text-white/50">
                    {currentItem.fileName}
                  </span>
                )}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
});

export default MediaViewer;
